import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChangedIcon from "@mui/icons-material/EditOutlined";
import BookedIcon from "@mui/icons-material/ThumbUpOffAlt";
import CreatedIcon from "@mui/icons-material/AddCircleOutline";
import CanceledIcon from "@mui/icons-material/HighlightOff";
import DeclinedIcon from "@mui/icons-material/ThumbDownOffAlt";
import { Box, Card, Chip, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import { diff } from "deep-object-diff";
import { DateTime } from "luxon";
import { ReactElement, useMemo, useState } from "react";
import { useGetOne } from "react-admin";
import { AutovioCalendarEvent, isCourseLesson } from "../../model/autovioCalendarEvents";
import { Badge2 } from "../../misc/Badge2";
import { UserDisplayName } from "../../misc/UserDisplayName";
import type { AutovioCalendarEventHistory } from "../../providers/calendarEventHistoryProvider";
import { formatDateTime } from "../../utils/calendar";
import { EventInfoCard } from "./EventInfoCard";

export function EventChangeLog(props: {
  event: AutovioCalendarEvent;
  history?: AutovioCalendarEventHistory | null;
  initiallyExpanded?: boolean;
}) {
  const { event, history, initiallyExpanded } = props;

  // TODO: implement change log for CourseLesson ...
  return isCourseLesson(event) ? null : (
    <_EventChangeLog event={event} history={history} initiallyExpanded={initiallyExpanded} />
  );
}

function _EventChangeLog(props: {
  event: AutovioCalendarEvent;
  history?: AutovioCalendarEventHistory | null;
  initiallyExpanded?: boolean;
}) {
  const { event, history: providedHistory, initiallyExpanded } = props;
  const [isExpanded, setIsExpanded_] = useState(initiallyExpanded ?? location.search.includes("showHistory"));
  const setIsExpanded = (value: boolean) => {
    // Side effect: update browser URL ...
    const url = new URL(location.href);
    if (value) {
      url.searchParams.set("showHistory", "1");
      browserHistory.replace(url.toString());
    } else {
      url.searchParams.delete("showHistory");
      browserHistory.replace(url.toString());
    }
    setIsExpanded_(value);
  };
  const { data: loadedHistory } = useGetOne<AutovioCalendarEventHistory>(
    "calendarEventHistory",
    { id: event.id, meta: { drivingSchoolId: event.drivingSchoolId } },
    { enabled: !!event.updatedAt && providedHistory === undefined },
  );
  const history = providedHistory === undefined ? loadedHistory : providedHistory;

  if (!event.updatedAt || event.updatedAt.valueOf() === event.createdAt.valueOf()) {
    return null;
  }

  const { events, changes } = history ?? {};
  const originalEvent = events?.at(-1);

  if (!changes || changes.length === 0) {
    return null;
  }

  return (
    <Stack direction="column" spacing="1em">
      <Stack
        direction="row"
        justifyContent="space-between"
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontSize="1rem">
            Änderungsverlauf
          </Typography>
          <Badge2 badgeContent={changes.length} />
        </Stack>
        <Box>
          <IconButton>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
      </Stack>
      <Collapse in={isExpanded} enter={false} exit={false} unmountOnExit>
        <Stack direction="column" spacing="1em">
          {event.deletedAt && event.deletedBy && (
            <_EventChangeLogAction what="Abgesagt" at={event.deletedAt} by={event.deletedBy} />
          )}
          {event.student?.canceledAt && (
            <_EventChangeLogAction what="Abgesagt" at={event.student.canceledAt} by={event.student.uid} />
          )}
          {event.student?.declinedAt && (
            <_EventChangeLogAction what="Einladung abgelehnt" at={event.student.declinedAt} by={event.student.uid} />
          )}
          {event.student?.bookedAt && event.student.bookedAt > (event.updatedAt ?? event.createdAt) && (
            <_EventChangeLogAction what="Gebucht" at={event.student.bookedAt} by={event.student.uid} />
          )}
          {changes?.flatMap(([oldEvent, newEvent], i) => {
            const a: Array<ReactElement> = [];
            if (i > 0 && newEvent.student?.rsvp === "pending") {
              // Check if the old event of the last change was booked ...
              const bookedAt = changes[i - 1][0].student?.bookedAt;
              if (bookedAt) {
                a.push(
                  <_EventChangeLogAction
                    key={`booked_${i - 1}_${i}`}
                    what="Gebucht"
                    at={bookedAt}
                    by={changes[i - 1][0].student.uid}
                  />,
                );
              }
            }
            a.push(<_EventChangeLogEntry key={`change_${i}`} oldEvent={oldEvent} newEvent={newEvent} />);
            return a;
          })}
          {originalEvent && originalEvent.createdBy === originalEvent.student?.uid && (
            <_EventChangeLogAction what="Erstellt/Gebucht" at={originalEvent.createdAt} by={originalEvent.createdBy} />
          )}
          {originalEvent && originalEvent.createdBy !== originalEvent.student?.uid && (
            <_EventChangeLogAction what="Erstellt" at={originalEvent.createdAt} by={originalEvent.createdBy} />
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
}

function _EventChangeLogEntry(props: { oldEvent: AutovioCalendarEvent; newEvent: AutovioCalendarEvent }) {
  const { oldEvent, newEvent } = props;

  const changedKeys = useMemo(() => {
    const eventDiff = diff(oldEvent, newEvent);
    const changedKeys = Object.keys(eventDiff);

    // Determine if the event duration has changed
    const oldDuration = oldEvent.end.diff(oldEvent.start);
    const newDuration = newEvent.end.diff(newEvent.start);
    if (!oldDuration.equals(newDuration)) {
      changedKeys.push("duration");
    }

    // Determine if the booked training has changed
    const oldBookedTrainingId = oldEvent.student?.bookedTrainingId;
    const newBookedTrainingId = newEvent.student?.bookedTrainingId;
    if (oldBookedTrainingId !== newBookedTrainingId) {
      changedKeys.push("bookedTrainingId");
    }

    return changedKeys;
  }, [oldEvent.id, newEvent.id]);

  return (
    <Card sx={{ bgcolor: newEvent.editReason ? "destructiveAction.light" : "action.hover" }}>
      <Stack direction="column">
        <Typography variant="body2" px="16px" py="10px">
          <ChangedIcon sx={{ mt: "3px", mb: "-3px", mr: "3px", fontSize: "16px" }} />
          Geändert am {<b>{formatDateTime(newEvent.updatedAt ?? newEvent.createdAt)}</b>} von{" "}
          {
            <UserDisplayName
              component="span"
              userId={newEvent.updatedBy ?? newEvent.createdBy}
              variant="body2"
              fontWeight="bold"
            />
          }
          {newEvent.editReason?.trim() ? (
            <>
              {" (Grund: "}
              <b>{newEvent.editReason.trim()}</b>
              {")"}
            </>
          ) : null}
        </Typography>
        <Divider />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: "16px", sm: "24px" }} p="16px">
          <Stack flex="1" minWidth={0} spacing="12px">
            <Typography variant="caption" color="text.disabled">
              Vorher:
            </Typography>
            <EventInfoCard
              event={oldEvent}
              mode="beforeChange"
              changedKeys={changedKeys}
              chip={<Chip label="Vorher" size="small" sx={{ bgcolor: "#fff0c8" }} />}
            />
          </Stack>
          <Divider sx={{ display: { xs: "block", sm: "none" } }} />
          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
          <Stack flex="1" minWidth={0} spacing="12px">
            <Typography variant="caption" color="text.disabled">
              Nachher:
            </Typography>
            <EventInfoCard
              event={newEvent}
              mode="afterChange"
              changedKeys={changedKeys}
              chip={<Chip label="Nachher" size="small" sx={{ bgcolor: "#d3f9e0" }} />}
            />
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}

function _EventChangeLogAction(props: {
  what?: "Erstellt" | "Erstellt/Gebucht" | "Gebucht" | "Einladung abgelehnt" | "Abgesagt";
  at: DateTime;
  by: string;
}) {
  const { what, at, by } = props;
  const iconSx = { mt: "3px", mb: "-3px", mr: "3px", fontSize: "16px" };
  return (
    <Card sx={{ bgcolor: "action.hover" }}>
      <Typography variant="body2" px="16px" py="10px">
        {what === "Erstellt" && <CreatedIcon sx={iconSx} />}
        {what === "Erstellt/Gebucht" && <BookedIcon sx={iconSx} />}
        {what === "Gebucht" && <BookedIcon sx={iconSx} />}
        {what === "Einladung abgelehnt" && <DeclinedIcon sx={iconSx} />}
        {what === "Abgesagt" && <CanceledIcon sx={iconSx} />}
        {what} am {<b>{formatDateTime(at)}</b>} von{" "}
        {<UserDisplayName component="span" userId={by} variant="body2" fontWeight="bold" />}
      </Typography>
    </Card>
  );
}
